import React, { useEffect } from "react";

import { faAngleDown } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faPlus } from "@performyard/fontawesome/pro-regular-svg-icons";
import { ButtonIcon, ButtonStandard } from "@performyard/py-components";
import { ActionItemModelJson, AgendaItemModelJson } from "@performyard/service-contracts";

import ChecklistItem from "./ChecklistItem";
import { ItemStatus } from "./ChecklistItem";

import { useMeetingContext } from "../Meetings/MeetingContext";

type ChecklistItem = ActionItemModelJson | AgendaItemModelJson;

interface ChecklistAccordionProps {
	title: string;
	items: ChecklistItem[];
	isLoading: boolean;
	defaultExpanded?: boolean;
	isAddingItem: boolean;
	newItemName: string;
	editingItemId: string | null;
	editingItemCurrentValue: string;
	setNewItemName: (value: string) => void;
	setIsAddingItem: (value: boolean) => void;
	startEditingItem: (id: string, value: string) => void;
	stopEditingItem: (save: boolean) => void;
	updateEditingItemValue: (value: string) => void;
	handleAddItem: (name: string) => void;
	handleAddButtonClick: () => void;
	handleStatusChange: (itemId: string, status: ItemStatus) => void;
	handleAssignmentClick?: (args: {
		itemId: string;
		meetingId: string;
		currentAssigneeId?: string;
		dueDate?: Date | null;
		name?: string;
	}) => void;
	itemType: "agenda" | "action";
}

const ChecklistAccordion = ({
	title,
	items,
	isLoading,
	defaultExpanded = true,
	isAddingItem,
	newItemName,
	editingItemId,
	editingItemCurrentValue,
	setNewItemName,
	setIsAddingItem,
	startEditingItem,
	stopEditingItem,
	updateEditingItemValue,
	handleAddItem,
	handleAddButtonClick,
	handleStatusChange,
	handleAssignmentClick,
	itemType,
}: ChecklistAccordionProps) => {
	const { meeting, userId, workspace, isInteractionDisabled } = useMeetingContext();
	const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);
	const contentRef = React.useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (contentRef.current) {
			// Only scroll to bottom if we're already near the bottom
			const isNearBottom =
				contentRef.current.scrollHeight - contentRef.current.scrollTop - contentRef.current.clientHeight < 50;
			if (isNearBottom) {
				contentRef.current.scrollTop = contentRef.current.scrollHeight;
			}
		}
	}, [items, isAddingItem]);

	const renderItem = (item: ChecklistItem) => {
		const status = item.meetings.find((m) => m.meetingId === meeting._id)?.status || "incomplete";
		const isEditing = editingItemId === item._id;
		const assigneeId =
			itemType === "action" && "participants" in item
				? item.participants?.find((p) => p.role === "assignee")?.userId
				: undefined;
		const assigneeName = assigneeId
			? workspace?.participants?.find((p) => p.userId === assigneeId)?.name
			: undefined;

		return (
			<ChecklistItem
				key={item._id}
				id={item._id}
				name={item.name}
				dueDate={itemType === "action" && "dueDate" in item ? item.dueDate : undefined}
				status={status}
				creatorId={item.created.userId}
				userId={userId}
				isEditing={isEditing}
				assigneeId={assigneeId}
				assigneeName={assigneeName}
				meetingId={meeting._id}
				itemType={itemType}
				onEdit={startEditingItem}
				onNameChange={updateEditingItemValue}
				onStatusChange={(newStatus) => handleStatusChange(item._id, newStatus)}
				onSave={stopEditingItem}
				currentEditValue={isEditing ? editingItemCurrentValue : item.name}
				onAssignmentClick={handleAssignmentClick}
			/>
		);
	};

	const renderAddForm = () => {
		if (!isAddingItem) return null;

		return (
			<ChecklistItem
				id="new-item"
				name=""
				status="incomplete"
				creatorId={userId}
				userId={userId}
				isEditing={true}
				isNewItem={true}
				meetingId={meeting._id}
				itemType={itemType}
				onEdit={() => {}}
				onNameChange={setNewItemName}
				onStatusChange={() => {}}
				onSave={(save) => {
					if (save && newItemName.trim()) {
						handleAddItem(newItemName);
					} else {
						setIsAddingItem(false);
						setNewItemName("");
					}
				}}
				currentEditValue={newItemName}
			/>
		);
	};

	if (isLoading) {
		return (
			<div className="flex flex-col rounded-lg border-[1px] border-solid border-neutral-3">
				<div
					className={`flex h-12 select-none items-center justify-between bg-neutral-1 px-4 hover:cursor-pointer hover:bg-neutral-2 ${
						isExpanded ? "rounded-t-lg" : "rounded-lg"
					}`}
					onClick={() => setIsExpanded((prev) => !prev)}
				>
					<div className="flex items-center space-x-2">
						<span className="text-paragraph-3 font-semibold">{title}</span>
					</div>
					<div
						className={
							isExpanded ? "rotate-180 transition duration-300" : "rotate-0 transition duration-300"
						}
					>
						<ButtonIcon
							icon={faAngleDown}
							onClick={(e) => {
								e.stopPropagation();
								setIsExpanded((prev) => !prev);
							}}
						/>
					</div>
				</div>
				{isExpanded && (
					<div className="flex flex-col space-y-2 p-4">
						{Array.from({ length: 2 }).map((_, index) => (
							<div key={index} className="flex items-center space-x-3 rounded-md bg-neutral-1 p-3">
								<div className="h-5 w-5 animate-pulse rounded bg-neutral-3" />
								<div className="flex-1">
									<div className="h-5 w-2/3 animate-pulse rounded bg-neutral-3" />
								</div>
								<div className="h-5 w-5 animate-pulse rounded bg-neutral-3" />
								<div className="h-5 w-5 animate-pulse rounded bg-neutral-3" />
							</div>
						))}
						<div className="flex justify-end pt-2">
							<div className="h-9 w-32 animate-pulse rounded bg-neutral-3" />
						</div>
					</div>
				)}
			</div>
		);
	}

	return (
		<div
			className={`flex flex-col rounded-lg border-[1px] border-solid border-neutral-3 ${
				isExpanded ? "min-h-0 flex-1" : "h-12"
			}`}
		>
			<div
				className={`flex h-12 select-none items-center justify-between bg-neutral-1 px-4 hover:cursor-pointer hover:bg-neutral-2 ${
					isExpanded ? "rounded-t-lg" : "rounded-lg"
				}`}
				onClick={() => setIsExpanded((prev) => !prev)}
			>
				<div className="flex items-center space-x-2">
					<span className="text-paragraph-3 font-semibold">{title}</span>
				</div>
				<div className={isExpanded ? "rotate-180 transition duration-300" : "rotate-0 transition duration-300"}>
					<ButtonIcon
						icon={faAngleDown}
						onClick={(e) => {
							e.stopPropagation();
							setIsExpanded((prev) => !prev);
						}}
					/>
				</div>
			</div>
			{isExpanded && (
				<div className="min-h-0 flex-1">
					<div ref={contentRef} className="h-full overflow-y-auto">
						<div className="flex flex-col space-y-2 p-4">
							{items.map(renderItem)}
							{renderAddForm()}
							<div className="flex justify-end pt-2">
								<div>
									<ButtonStandard
										iconLeft={faPlus}
										onClick={handleAddButtonClick}
										disabled={
											isInteractionDisabled ||
											editingItemId !== null ||
											(isAddingItem && !newItemName.trim())
										}
										dangerouslySetClassName={
											isInteractionDisabled ||
											editingItemId !== null ||
											(isAddingItem && !newItemName.trim())
												? "disabled:cursor-not-allowed disabled:hover:cursor-not-allowed disabled:pointer-events-auto disabled:hover:bg-primary-5 disabled:active:bg-primary-5"
												: ""
										}
									>
										Add {itemType === "agenda" ? "Agenda" : "Action"} Item
									</ButtonStandard>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default ChecklistAccordion;
